import React, { createContext, useState, useContext, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

export const translations = {
  en: {
    // Header
    features: 'Features',
    platforms: 'Platforms',
    pricing: 'Pricing',
    login: 'Log In',
    getStarted: 'Get Started',
    
    // Hero
    heroTitle: 'Automate Your Customer Conversations',
    heroSubtitle: 'Build powerful chatbots for Facebook Messenger, Instagram, and WhatsApp. No coding required.',
    startFreeTrial: 'Start Free Trial',
    watchDemo: 'Watch Demo',
    noCreditCard: 'No credit card required',
    freeTrial: '7-day free trial',
    
    // Platforms
    platformsTitle: 'Connect with Your Customers Everywhere',
    platformsSubtitle: 'One platform to manage conversations across all major messaging channels',
    messenger: 'Facebook Messenger',
    messengerDesc: 'Reach 1.3B+ active users on Messenger',
    instagram: 'Instagram DM',
    instagramDesc: "Engage with Instagram's visual audience",
    whatsapp: 'WhatsApp Business',
    whatsappDesc: 'Connect with 2B+ WhatsApp users',
    
    // Features
    featuresTitle: 'Everything You Need to Grow',
    featuresSubtitle: 'Powerful features to automate and scale your customer engagement',
    visualFlowBuilder: 'Visual Flow Builder',
    visualFlowDesc: 'Drag-and-drop interface to create complex automation flows without coding',
    contactManagement: 'Contact Management',
    contactManagementDesc: 'Organize and segment your audience with tags and custom fields',
    analyticsInsights: 'Analytics & Insights',
    analyticsInsightsDesc: 'Track performance with detailed metrics and real-time reporting',
    
    // CTA
    ctaTitle: 'Ready to Transform Your Customer Engagement?',
    ctaSubtitle: 'Join thousands of businesses using ChatFlow to automate conversations and grow faster',
    
    // Pricing Page
    choosePlan: 'Choose Your Perfect Plan',
    pricingSubtitle: 'Start with a 7-day free trial. No credit card required.',
    allPlansInclude: 'All plans include 7-day free trial',
    
    // Plans
    free: 'Free',
    freeDesc: 'Stay connected',
    starter: 'Starter',
    starterDesc: 'Best for growing teams',
    professional: 'Professional',
    professionalDesc: 'Perfect for established businesses',
    enterprise: 'Enterprise',
    enterpriseDesc: 'For large scale operations',
    mostPopular: 'Most Popular',
    
    // Plan Features
    contacts: 'contacts',
    connectChannels: 'Connect',
    channels: 'channels',
    users: 'users',
    user: 'user',
    emailSupport: 'Email support',
    basicAnalytics: 'Basic analytics',
    noBranding: 'No ChatFlow branding',
    chatFlowBranding: 'Chat Flow branding: "Powered by ChatFlow" appears in messages',
    billedYearly: 'billed yearly',
    off: 'off',
    or: 'or',
    monthly: 'monthly',
    
    // FAQ
    faqTitle: 'Frequently Asked Questions',
    faqQ1: 'Can I change plans later?',
    faqA1: 'Yes! You can upgrade or downgrade your plan at any time. Changes will be reflected in your next billing cycle.',
    faqQ2: 'What payment methods do you accept?',
    faqA2: 'We accept credit cards, debit cards, PayPal, and Stripe payments. All transactions are secure and encrypted.',
    faqQ3: 'Can I cancel anytime?',
    faqA3: 'Absolutely! You can cancel your subscription at any time with no cancellation fees. Your service will continue until the end of your billing period.',
    
    // Auth
    welcomeBack: 'Welcome back',
    loginDesc: 'Log in to your account',
    createAccount: 'Create your account',
    signupDesc: 'Start your 7-day free trial',
    fullName: 'Full name',
    emailAddress: 'Email address',
    password: 'Password',
    phoneNumber: 'Phone Number',
    dontHaveAccount: "Don't have an account?",
    alreadyHaveAccount: 'Already have an account?',
    signUp: 'Sign up',
    createAccountBtn: 'Create Account',
    
    // Dashboard
    dashboard: 'Dashboard',
    welcomeBackDash: "Welcome back! Here's what's happening.",
    createNewFlow: 'Create New Flow',
    totalSubscribers: 'Total Subscribers',
    activeFlows: 'Active Flows',
    messagesSent: 'Messages Sent',
    conversionRate: 'Conversion Rate',
    recentFlows: 'Recent Flows',
    latestFlows: 'Your latest automation flows',
    platformDistribution: 'Platform Distribution',
    subscribersByPlatform: 'Subscribers by platform',
    subscribers: 'subscribers',
    active: 'active',
    draft: 'draft',
    
    // Other pages
    flows: 'Flows',
    contacts: 'Contacts',
    analytics: 'Analytics',
    liveChat: 'Live Chat',
    settings: 'Settings',
    logOut: 'Log Out',
    
    // Checkout
    secureCheckout: 'Secure Checkout',
    personalInfo: 'Personal Information',
    personalInfoDesc: 'Please provide your contact details',
    billingAddress: 'Billing Address',
    billingAddressDesc: 'Where should we send your invoice?',
    paymentMethod: 'Payment Method',
    paymentMethodDesc: 'Choose your preferred payment method',
    orderSummary: 'Order Summary',
    selectedPlan: 'Selected Plan',
    monthlyPrice: 'Monthly Price',
    dueToday: 'Due Today',
    freeTrialInfo: "You won't be charged today. After 7 days, you'll be billed",
    cancelAnytime: 'Cancel anytime',
    agreeTerms: 'By continuing, you agree to our Terms of Service and Privacy Policy.',
    
    // Footer
    product: 'Product',
    company: 'Company',
    resources: 'Resources',
    legal: 'Legal',
    about: 'About',
    blog: 'Blog',
    careers: 'Careers',
    documentation: 'Documentation',
    helpCenter: 'Help Center',
    api: 'API',
    privacy: 'Privacy',
    terms: 'Terms',
    security: 'Security',
    templates: 'Templates',
    allRightsReserved: 'All rights reserved',
    
    // Templates Page
    chatbotTemplates: 'Chatbot Templates',
    templatesSubtitle: 'Start with pre-built templates and customize them for your business',
    allTemplates: 'All Templates',
    ecommerce: 'E-commerce',
    marketing: 'Marketing',
    engagement: 'Engagement',
    scheduling: 'Scheduling',
    platforms: 'Platforms',
    useCases: 'Use Cases',
    useThisTemplate: 'Use This Template',
    cantFindTemplate: "Can't Find What You Need?",
    createCustomFlow: 'Create a custom flow from scratch with our visual flow builder',
    startBuildingNow: 'Start Building Now',
    
    // Template Names
    welcomeMessage: 'Welcome Message',
    welcomeMessageDesc: 'Greet new subscribers and introduce your brand',
    abandonedCart: 'Abandoned Cart Recovery',
    abandonedCartDesc: 'Recover lost sales with automated follow-ups',
    appointmentBooking: 'Appointment Booking',
    appointmentBookingDesc: 'Schedule appointments automatically with customers',
    leadGeneration: 'Lead Generation',
    leadGenerationDesc: 'Capture and qualify leads automatically',
    orderStatus: 'Order Status Updates',
    orderStatusDesc: 'Keep customers informed about their orders',
    newsletter: 'Newsletter Subscription',
    newsletterDesc: 'Grow your email list with automated opt-ins',
    customerFeedback: 'Customer Feedback',
    customerFeedbackDesc: 'Collect reviews and feedback from customers',
    flashSale: 'Flash Sale Announcement',
    flashSaleDesc: 'Notify subscribers about limited-time offers'
  },
  pt: {
    // Header
    features: 'Recursos',
    platforms: 'Plataformas',
    pricing: 'Preços',
    login: 'Entrar',
    getStarted: 'Começar',
    
    // Hero
    heroTitle: 'Automatize Suas Conversas com Clientes',
    heroSubtitle: 'Crie chatbots poderosos para Facebook Messenger, Instagram e WhatsApp. Sem necessidade de programação.',
    startFreeTrial: 'Iniciar Teste Grátis',
    watchDemo: 'Ver Demo',
    noCreditCard: 'Sem cartão de crédito',
    freeTrial: 'Teste grátis de 7 dias',
    
    // Platforms
    platformsTitle: 'Conecte-se com Seus Clientes em Todos os Lugares',
    platformsSubtitle: 'Uma plataforma para gerenciar conversas em todos os principais canais de mensagens',
    messenger: 'Facebook Messenger',
    messengerDesc: 'Alcance 1,3B+ usuários ativos no Messenger',
    instagram: 'Instagram DM',
    instagramDesc: 'Engaje com o público visual do Instagram',
    whatsapp: 'WhatsApp Business',
    whatsappDesc: 'Conecte-se com 2B+ usuários do WhatsApp',
    
    // Features
    featuresTitle: 'Tudo o Que Você Precisa para Crescer',
    featuresSubtitle: 'Recursos poderosos para automatizar e escalar o engajamento com seus clientes',
    visualFlowBuilder: 'Construtor Visual de Fluxos',
    visualFlowDesc: 'Interface arrastar e soltar para criar fluxos de automação complexos sem programação',
    contactManagement: 'Gerenciamento de Contatos',
    contactManagementDesc: 'Organize e segmente seu público com tags e campos personalizados',
    analyticsInsights: 'Análises e Insights',
    analyticsInsightsDesc: 'Acompanhe o desempenho com métricas detalhadas e relatórios em tempo real',
    
    // CTA
    ctaTitle: 'Pronto para Transformar o Engajamento com Seus Clientes?',
    ctaSubtitle: 'Junte-se a milhares de empresas usando o ChatFlow para automatizar conversas e crescer mais rápido',
    
    // Pricing Page
    choosePlan: 'Escolha Seu Plano Perfeito',
    pricingSubtitle: 'Comece com um teste grátis de 7 dias. Sem cartão de crédito.',
    allPlansInclude: 'Todos os planos incluem teste grátis de 7 dias',
    
    // Plans
    free: 'Grátis',
    freeDesc: 'Mantenha-se conectado',
    starter: 'Inicial',
    starterDesc: 'Melhor para equipes em crescimento',
    professional: 'Profissional',
    professionalDesc: 'Perfeito para empresas estabelecidas',
    enterprise: 'Empresarial',
    enterpriseDesc: 'Para operações em grande escala',
    mostPopular: 'Mais Popular',
    
    // Plan Features
    contacts: 'contatos',
    connectChannels: 'Conectar',
    channels: 'canais',
    users: 'usuários',
    user: 'usuário',
    emailSupport: 'Suporte por email',
    basicAnalytics: 'Análises básicas',
    noBranding: 'Sem marca ChatFlow',
    chatFlowBranding: 'Marca ChatFlow: "Powered by ChatFlow" aparece nas mensagens',
    billedYearly: 'cobrado anualmente',
    off: 'desconto',
    or: 'ou',
    monthly: 'mensal',
    
    // FAQ
    faqTitle: 'Perguntas Frequentes',
    faqQ1: 'Posso mudar de plano depois?',
    faqA1: 'Sim! Você pode fazer upgrade ou downgrade do seu plano a qualquer momento. As alterações serão refletidas no próximo ciclo de cobrança.',
    faqQ2: 'Quais formas de pagamento vocês aceitam?',
    faqA2: 'Aceitamos cartões de crédito, cartões de débito, PayPal e pagamentos Stripe. Todas as transações são seguras e criptografadas.',
    faqQ3: 'Posso cancelar a qualquer momento?',
    faqA3: 'Absolutamente! Você pode cancelar sua assinatura a qualquer momento sem taxas de cancelamento. Seu serviço continuará até o final do período de cobrança.',
    
    // Auth
    welcomeBack: 'Bem-vindo de volta',
    loginDesc: 'Entre na sua conta',
    createAccount: 'Crie sua conta',
    signupDesc: 'Inicie seu teste grátis de 7 dias',
    fullName: 'Nome completo',
    emailAddress: 'Endereço de email',
    password: 'Senha',
    phoneNumber: 'Número de telefone',
    dontHaveAccount: 'Não tem uma conta?',
    alreadyHaveAccount: 'Já tem uma conta?',
    signUp: 'Cadastre-se',
    createAccountBtn: 'Criar Conta',
    
    // Dashboard
    dashboard: 'Painel',
    welcomeBackDash: 'Bem-vindo de volta! Aqui está o que está acontecendo.',
    createNewFlow: 'Criar Novo Fluxo',
    totalSubscribers: 'Total de Inscritos',
    activeFlows: 'Fluxos Ativos',
    messagesSent: 'Mensagens Enviadas',
    conversionRate: 'Taxa de Conversão',
    recentFlows: 'Fluxos Recentes',
    latestFlows: 'Seus últimos fluxos de automação',
    platformDistribution: 'Distribuição por Plataforma',
    subscribersByPlatform: 'Inscritos por plataforma',
    subscribers: 'inscritos',
    active: 'ativo',
    draft: 'rascunho',
    
    // Other pages
    flows: 'Fluxos',
    contacts: 'Contatos',
    analytics: 'Análises',
    liveChat: 'Chat ao Vivo',
    settings: 'Configurações',
    logOut: 'Sair',
    
    // Checkout
    secureCheckout: 'Checkout Seguro',
    personalInfo: 'Informações Pessoais',
    personalInfoDesc: 'Por favor, forneça seus detalhes de contato',
    billingAddress: 'Endereço de Cobrança',
    billingAddressDesc: 'Para onde devemos enviar sua fatura?',
    paymentMethod: 'Método de Pagamento',
    paymentMethodDesc: 'Escolha seu método de pagamento preferido',
    orderSummary: 'Resumo do Pedido',
    selectedPlan: 'Plano Selecionado',
    monthlyPrice: 'Preço Mensal',
    dueToday: 'Devido Hoje',
    freeTrialInfo: 'Você não será cobrado hoje. Após 7 dias, você será cobrado',
    cancelAnytime: 'Cancele a qualquer momento',
    agreeTerms: 'Ao continuar, você concorda com nossos Termos de Serviço e Política de Privacidade.',
    
    // Footer
    product: 'Produto',
    company: 'Empresa',
    resources: 'Recursos',
    legal: 'Legal',
    about: 'Sobre',
    blog: 'Blog',
    careers: 'Carreiras',
    documentation: 'Documentação',
    helpCenter: 'Central de Ajuda',
    api: 'API',
    privacy: 'Privacidade',
    terms: 'Termos',
    security: 'Segurança',
    templates: 'Modelos',
    allRightsReserved: 'Todos os direitos reservados',
    
    // Templates Page
    chatbotTemplates: 'Modelos de Chatbot',
    templatesSubtitle: 'Comece com modelos pré-construídos e personalize-os para seu negócio',
    allTemplates: 'Todos os Modelos',
    ecommerce: 'E-commerce',
    marketing: 'Marketing',
    engagement: 'Engajamento',
    scheduling: 'Agendamento',
    platforms: 'Plataformas',
    useCases: 'Casos de Uso',
    useThisTemplate: 'Usar Este Modelo',
    cantFindTemplate: 'Não Encontrou o Que Precisa?',
    createCustomFlow: 'Crie um fluxo personalizado do zero com nosso construtor visual de fluxos',
    startBuildingNow: 'Comece a Construir Agora',
    
    // Template Names
    welcomeMessage: 'Mensagem de Boas-Vindas',
    welcomeMessageDesc: 'Cumprimente novos inscritos e apresente sua marca',
    abandonedCart: 'Recuperação de Carrinho Abandonado',
    abandonedCartDesc: 'Recupere vendas perdidas com acompanhamentos automatizados',
    appointmentBooking: 'Agendamento de Compromissos',
    appointmentBookingDesc: 'Agende compromissos automaticamente com clientes',
    leadGeneration: 'Geração de Leads',
    leadGenerationDesc: 'Capture e qualifique leads automaticamente',
    orderStatus: 'Atualizações de Status de Pedido',
    orderStatusDesc: 'Mantenha os clientes informados sobre seus pedidos',
    newsletter: 'Assinatura de Newsletter',
    newsletterDesc: 'Aumente sua lista de e-mails com opt-ins automatizados',
    customerFeedback: 'Feedback de Clientes',
    customerFeedbackDesc: 'Colete avaliações e feedback de clientes',
    flashSale: 'Anúncio de Venda Relâmpago',
    flashSaleDesc: 'Notifique inscritos sobre ofertas por tempo limitado'
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('en');

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage) {
      setLanguage(savedLanguage);
    }
  }, []);

  const changeLanguage = (lang) => {
    setLanguage(lang);
    localStorage.setItem('language', lang);
  };

  const t = (key) => {
    return translations[language][key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, changeLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};
